// Mensagens (0.35): amigos, conversas a dois e em grupo, fotos e arquivos, tudo em tempo real pelo Supabase.
// As mensagens chegam na hora (Realtime) enquanto o app está aberto; com o app fechado, chega o aviso no celular.
import { useSyncExternalStore } from 'react';
import { supabase } from './supabase';
import { DEMO } from './env';
import { uid } from './date';
import { t } from './i18n';
import { definirConversaAberta } from '../notifications';
import { AppState, Platform } from 'react-native';
import { avisarAgora, temPush } from './push';
import type { Fig } from '../store/types';

export type Conversa = { id: string; tipo: 'dupla' | 'grupo'; nome: string; grupo_id: string | null; outro: string | null;
  ultima_em: string; ultima_txt: string; ultima_autor: string | null; nao_lidas: number; silenciada: boolean; membros: number; admin: boolean; arquivada?: boolean };
export type Anexo = { path: string; mime: string; nome: string; tam?: number; chamada?: string; midia?: 'audio' | 'video';
  fig?: Omit<Fig, 'id'>; url?: string; w?: number; h?: number };   // figurinha e GIF (0.51)
export type Mensagem = { id: string; conversa_id: string; autor: string | null; autor_nome: string; tipo: 'texto' | 'foto' | 'video' | 'audio' | 'arquivo' | 'chamada' | 'sistema' | 'figurinha' | 'gif';
  texto: string; anexo: Anexo | null; resposta_a: string | null; criado_em: string; apagada: boolean; enviando?: boolean; erro?: boolean };
export type Amigo = { id: string; nome: string; status: 'pendente' | 'aceita' | 'bloqueada'; eu_pedi: boolean };
export type Tocando = { conversa: string; chamada: string; de: string; midia: 'audio' | 'video'; nome: string } | null;

// ---------- estado (fora do React; as telas assinam) ----------
type Estado = { eu: string | null; conversas: Conversa[] | null; erro: string | null; msgs: Record<string, Mensagem[]>; amigos: Amigo[] | null; tocando: Tocando };
let st: Estado = { eu: null, conversas: null, erro: null, msgs: {}, amigos: null, tocando: null };
const ouv = new Set<() => void>();
const set = (p: Partial<Estado>) => { st = { ...st, ...p }; ouv.forEach(f => f()); };
const assinar = (f: () => void) => { ouv.add(f); return () => { ouv.delete(f); }; };
export const useChat = () => useSyncExternalStore(assinar, () => st, () => st);
export const naoLidasTotal = (s: Estado = st) => (s.conversas ?? []).filter(c => !c.silenciada && !c.arquivada).reduce((n, c) => n + c.nao_lidas, 0);
/** Conversa aberta na tela (não conta como não lida e não mostra o aviso dela). */
export const aberta = { id: null as string | null };
definirConversaAberta(() => aberta.id);

const precisaSql = (e: any) => /function|relation|does not exist|schema cache|PGRST20/i.test(String(e?.message ?? e ?? ''));
export const erroAmigavel = (e: any) => (precisaSql(e) ? t('O servidor ainda não está preparado para as mensagens. Rode o 17-LIGAR-MENSAGENS.bat.') : String(e?.message ?? e));

// ---------- prévia de demonstração (sem servidor) ----------
const agora = () => new Date().toISOString();
const antes = (min: number) => new Date(Date.now() - min * 60000).toISOString();
function demoInicial() {
  const c = (id: string, tipo: 'dupla' | 'grupo', nome: string, txt: string, min: number, nao: number, membros = 2): Conversa =>
    ({ id, tipo, nome, grupo_id: null, outro: tipo === 'dupla' ? 'u-' + id : null, ultima_em: antes(min), ultima_txt: txt, ultima_autor: 'u-' + id, nao_lidas: nao, silenciada: false, membros, admin: tipo === 'grupo' });
  const m = (cv: string, autor: string | null, nome: string, texto: string, min: number): Mensagem =>
    ({ id: uid(), conversa_id: cv, autor, autor_nome: nome, tipo: 'texto', texto, anexo: null, resposta_a: null, criado_em: antes(min), apagada: false });
  set({ eu: 'eu', conversas: [c('ana', 'dupla', 'Ana Beatriz', 'Já viu o roteiro de Lisboa?', 3, 2), c('turma', 'grupo', 'Turma de Inglês', 'Professora: aula às 19h', 40, 1, 12),
    c('fam', 'grupo', 'Família', '📷 Foto', 300, 0, 5)],
    msgs: { ana: [m('ana', 'eu', 'Walter', 'Oi! Tudo certo para sábado?', 20), m('ana', 'u-ana', 'Ana Beatriz', 'Tudo! Comprei os ingressos.', 5), m('ana', 'u-ana', 'Ana Beatriz', 'Já viu o roteiro de Lisboa?', 3)],
      turma: [m('turma', 'u-turma', 'Professora', 'Boa tarde, turma! Aula às 19h por vídeo aqui no grupo.', 40)] },
    amigos: [{ id: 'u-ana', nome: 'Ana Beatriz', status: 'aceita', eu_pedi: false }, { id: 'u-joao', nome: 'João', status: 'pendente', eu_pedi: false }] });
}

// ---------- conversas e mensagens ----------
let canal: any = null;
export async function iniciarChat(eu: string | null) {
  if (DEMO) { if (!st.conversas) demoInicial(); return; }
  if (!supabase || !eu) return;
  if (st.eu !== eu) set({ eu, conversas: null, msgs: {}, amigos: null });
  await carregarConversas();
  if (canal) return;
  // Canal particular de cada pessoa (u:<id>): o servidor manda só o que é dela. Aguenta muita gente ao mesmo tempo.
  try { await (supabase.realtime as any).setAuth?.(); } catch { /* segue */ }
  canal = supabase.channel('u:' + eu, { config: { private: true } })
    .on('broadcast', { event: 'msg' }, (p: any) => chegou(p.payload as Mensagem))
    .on('broadcast', { event: 'msg_mudou' }, (p: any) => mudou(p.payload as Mensagem))
    .on('broadcast', { event: 'membros' }, () => { carregarConversas().catch(() => {}); })
    // servidor ainda sem o banco 0.41: continua pelo jeito antigo
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'mensagens' }, (p: any) => chegou(p.new as Mensagem))
    .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'mensagens' }, (p: any) => mudou(p.new as Mensagem))
    .on('postgres_changes', { event: '*', schema: 'public', table: 'conversa_membros' }, () => { carregarConversas().catch(() => {}); })
    .subscribe();
}
export function pararChat() { if (canal) { supabase?.removeChannel(canal); canal = null; } set({ eu: null, conversas: null, msgs: {}, amigos: null, tocando: null }); }

export async function carregarConversas() {
  if (DEMO) return;
  if (!supabase) return;
  const { data, error } = await supabase.rpc('minhas_conversas');
  if (error) { set({ erro: erroAmigavel(error) }); return; }
  set({ conversas: (data as Conversa[]).map(c => (c.id === aberta.id ? { ...c, nao_lidas: 0 } : c)), erro: null });
}

const vistas = new Set<string>();   // a mesma mensagem pode chegar por dois caminhos: conta uma vez só
function chegou(m: Mensagem) {
  if (!m?.id || vistas.has(m.id)) return;
  vistas.add(m.id);
  if (vistas.size > 2000) vistas.clear();
  const l = st.msgs[m.conversa_id];
  if (l) {
    // troca a mensagem provisória (enviando) pela do servidor
    const i = l.findIndex(x => x.enviando && x.autor === m.autor && x.texto === m.texto && x.tipo === m.tipo);
    const nova = i >= 0 ? l.map((x, j) => (j === i ? m : x)) : l.some(x => x.id === m.id) ? l : [...l, m];
    set({ msgs: { ...st.msgs, [m.conversa_id]: nova } });
  }
  const meu = m.autor === st.eu;
  const cs = st.conversas;
  if (cs && cs.some(c => c.id === m.conversa_id)) {
    const resumo = resumoDe(m);
    set({ conversas: cs.map(c => (c.id !== m.conversa_id ? c : { ...c, ultima_em: m.criado_em, ultima_txt: resumo, ultima_autor: m.autor,
      nao_lidas: meu || aberta.id === c.id || m.tipo === 'sistema' ? c.nao_lidas : c.nao_lidas + 1 })).sort((a, b) => b.ultima_em.localeCompare(a.ultima_em)) });
    if (aberta.id === m.conversa_id && !meu) marcarLida(m.conversa_id);
  } else carregarConversas().catch(() => {});
  // Aviso feito pelo próprio aparelho quando este celular não recebe o aviso do servidor (APK sem Firebase):
  // funciona com o app aberto em outra tela ou recém-minimizado (enquanto o Android mantém a conexão).
  if (!meu && !DEMO && Platform.OS !== 'web' && !temPush() && m.tipo !== 'sistema') {
    const c = st.conversas?.find(x => x.id === m.conversa_id);
    const fora = AppState.currentState !== 'active';
    if ((fora || aberta.id !== m.conversa_id) && ((!c?.silenciada && !c?.arquivada) || m.tipo === 'chamada') && (fora || m.tipo !== 'chamada')) {
      const resumo = m.tipo === 'chamada' ? t('Ligando. Toque para atender.') : resumoDe(m);
      const titulo = m.tipo === 'chamada' ? '📞 ' + m.autor_nome : c?.tipo === 'grupo' ? c.nome : m.autor_nome;
      avisarAgora(titulo, c?.tipo === 'grupo' && m.tipo !== 'chamada' ? `${m.autor_nome}: ${resumo}` : resumo,
        { tipo: m.tipo === 'chamada' ? 'chamada' : 'mensagem', conversa: m.conversa_id, midia: m.anexo?.midia ?? null }, m.tipo === 'chamada' ? 'chamadas' : 'mensagens');
    }
  }
  // alguém está ligando (mensagem de chamada nova de outra pessoa)
  if (m.tipo === 'chamada' && !meu && m.anexo?.chamada && Date.now() - new Date(m.criado_em).getTime() < 60000) {
    const c = st.conversas?.find(x => x.id === m.conversa_id);
    set({ tocando: { conversa: m.conversa_id, chamada: m.anexo.chamada, de: m.autor_nome, midia: m.anexo.midia ?? 'video', nome: c?.nome ?? m.autor_nome } });
  }
}
/** Texto curto da mensagem (lista de conversas e avisos). */
export const resumoDe = (m: Mensagem) => m.tipo === 'foto' ? '📷 ' + t('Foto') : m.tipo === 'arquivo' ? '📎 ' + (m.anexo?.nome ?? t('Arquivo'))
  : m.tipo === 'chamada' ? '📞 ' + m.texto : m.tipo === 'figurinha' ? '🎉 ' + t('Figurinha') : m.tipo === 'gif' ? '🎞️ GIF'
  : m.tipo === 'video' ? '🎬 ' + t('Vídeo') : m.tipo === 'audio' ? '🎤 ' + t('Áudio') : m.texto;
function mudou(m: Mensagem) {
  const l = st.msgs[m.conversa_id];
  if (l) set({ msgs: { ...st.msgs, [m.conversa_id]: l.map(x => (x.id === m.id ? m : x)) } });
}

export async function carregarMensagens(conv: string, antesDe?: string) {
  if (DEMO) { if (!st.msgs[conv]) set({ msgs: { ...st.msgs, [conv]: [] } }); return true; }
  if (!supabase) return false;
  let q = supabase.from('mensagens').select('*').eq('conversa_id', conv).order('criado_em', { ascending: false }).limit(60);
  if (antesDe) q = q.lt('criado_em', antesDe);
  const { data, error } = await q;
  if (error) { set({ erro: erroAmigavel(error) }); return false; }
  const novas = (data as Mensagem[]).reverse();
  const atuais = antesDe ? st.msgs[conv] ?? [] : (st.msgs[conv] ?? []).filter(x => x.enviando || x.erro);
  set({ msgs: { ...st.msgs, [conv]: antesDe ? [...novas, ...atuais] : [...novas, ...atuais] } });
  return novas.length === 60;
}

export function marcarLida(conv: string) {
  set({ conversas: st.conversas?.map(c => (c.id === conv ? { ...c, nao_lidas: 0 } : c)) ?? null });
  if (!DEMO) supabase?.rpc('conversa_lida', { p_conversa: conv }).then(() => {}, () => {});
}

function provisoria(conv: string, p: Partial<Mensagem>): Mensagem {
  return { id: 'tmp-' + uid(), conversa_id: conv, autor: st.eu, autor_nome: '', tipo: 'texto', texto: '', anexo: null, resposta_a: null,
    criado_em: agora(), apagada: false, enviando: true, ...p };
}
const colocar = (m: Mensagem) => set({ msgs: { ...st.msgs, [m.conversa_id]: [...(st.msgs[m.conversa_id] ?? []), m] } });
const trocar = (conv: string, id: string, p: Partial<Mensagem> | null) =>
  set({ msgs: { ...st.msgs, [conv]: (st.msgs[conv] ?? []).flatMap(x => (x.id !== id ? [x] : p ? [{ ...x, ...p }] : [])) } });

export async function enviarTexto(conv: string, texto: string, resposta?: string | null) {
  const tx = texto.trim(); if (!tx) return;
  const m = provisoria(conv, { texto: tx, resposta_a: resposta ?? null });
  colocar(m);
  if (DEMO) { chegou({ ...m, enviando: false, id: uid() }); return; }
  const { data, error } = await supabase!.from('mensagens').insert({ conversa_id: conv, texto: tx, resposta_a: resposta ?? null }).select().single();
  if (error) trocar(conv, m.id, { enviando: false, erro: true });
  else trocar(conv, m.id, { ...(data as Mensagem), enviando: false });
}

/** Foto, vídeo ou arquivo: vai para o armazenamento "chat" (só quem está na conversa vê). */
export async function enviarArquivo(conv: string, arq: { bytes: Uint8Array; mime: string; nome: string }) {
  const tipo = arq.mime.startsWith('image/') ? 'foto' : arq.mime.startsWith('video/') ? 'video' : arq.mime.startsWith('audio/') ? 'audio' : 'arquivo';
  if (arq.bytes.length > 25 * 1024 * 1024) throw new Error(t('O arquivo passa de 25 MB.'));
  const nomeLimpo = arq.nome.replace(/[^\w.\-]+/g, '_').slice(-80) || 'arquivo';
  const path = `${conv}/${uid()}-${nomeLimpo}`;
  const m = provisoria(conv, { tipo, anexo: { path, mime: arq.mime, nome: arq.nome, tam: arq.bytes.length } });
  colocar(m);
  if (DEMO) { trocar(conv, m.id, { enviando: false }); return; }
  const up = await supabase!.storage.from('chat').upload(path, arq.bytes, { contentType: arq.mime, upsert: false });
  if (up.error) { trocar(conv, m.id, { enviando: false, erro: true }); throw new Error(erroAmigavel(up.error)); }
  const { data, error } = await supabase!.from('mensagens').insert({ conversa_id: conv, tipo, anexo: m.anexo }).select().single();
  if (error) trocar(conv, m.id, { enviando: false, erro: true });
  else trocar(conv, m.id, { ...(data as Mensagem), enviando: false });
}

// ─── figurinhas e GIFs (0.51) ───
async function enviarEspecial(conv: string, tipo: 'figurinha' | 'gif', texto: string, anexo: Anexo) {
  const m = provisoria(conv, { tipo, texto, anexo });
  colocar(m);
  if (DEMO) { trocar(conv, m.id, { enviando: false }); return; }
  const { data, error } = await supabase!.from('mensagens').insert({ conversa_id: conv, tipo, texto, anexo }).select().single();
  if (error) { trocar(conv, m.id, { enviando: false, erro: true }); throw new Error(erroAmigavel(error)); }
  trocar(conv, m.id, { ...(data as Mensagem), enviando: false });
}
/** Baixa um arquivo da conversa (para encaminhar ou reusar em outra conversa). */
async function bytesDoAnexo(path: string) {
  const u = await urlDoAnexo(path);
  if (!u) throw new Error(t('Não foi possível ler o arquivo.'));
  return new Uint8Array(await (await fetch(u)).arrayBuffer());
}
/** Sobe a foto da figurinha para a pasta da conversa. */
export async function subirFotoFigurinha(conv: string, bytes: Uint8Array) {
  const path = `${conv}/${uid()}-figurinha.jpg`;
  if (DEMO) return path;
  const up = await supabase!.storage.from('chat').upload(path, bytes, { contentType: 'image/jpeg', upsert: false });
  if (up.error) throw new Error(erroAmigavel(up.error));
  return path;
}
export async function enviarFigurinha(conv: string, f: Omit<Fig, 'id'> & { id?: string }) {
  const { id: _id, ...fig } = f;
  // figurinha de foto guardada de outra conversa: a foto é copiada para esta (cada conversa só vê os próprios arquivos)
  if (fig.img && !fig.img.startsWith(conv + '/')) fig.img = await subirFotoFigurinha(conv, await bytesDoAnexo(fig.img));
  await enviarEspecial(conv, 'figurinha', (fig.e ?? '') + (fig.t ? ' ' + fig.t : ''), { path: '', mime: 'application/x-figurinha', nome: '🎉 ' + t('Figurinha'), fig, ...(fig.url ? { url: fig.url } : {}) });
}
export const enviarGif = (conv: string, g: { url: string; w: number; h: number }) =>
  enviarEspecial(conv, 'gif', '', { path: '', mime: 'image/gif', nome: 'GIF', url: g.url, w: g.w, h: g.h });

/** Encaminha uma mensagem para outra conversa (texto, foto, arquivo, figurinha ou GIF). */
export async function encaminhar(m: Mensagem, para: string) {
  if (m.tipo === 'texto') return enviarTexto(para, m.texto);
  if (m.tipo === 'figurinha' && m.anexo?.fig) return enviarFigurinha(para, m.anexo.fig);
  if (m.tipo === 'gif' && m.anexo?.url) return enviarGif(para, { url: m.anexo.url, w: m.anexo.w ?? 200, h: m.anexo.h ?? 200 });
  if (m.anexo?.path) return enviarArquivo(para, { bytes: await bytesDoAnexo(m.anexo.path), mime: m.anexo.mime, nome: m.anexo.nome });
}

const urls = new Map<string, { url: string; ate: number }>();
/** Endereço temporário (1 hora) para ver a foto ou baixar o arquivo. */
export async function urlDoAnexo(path: string) {
  const c = urls.get(path);
  if (c && c.ate > Date.now()) return c.url;
  if (!supabase || DEMO) return null;
  const { data } = await supabase.storage.from('chat').createSignedUrl(path, 3600);
  if (data?.signedUrl) urls.set(path, { url: data.signedUrl, ate: Date.now() + 50 * 60000 });
  return data?.signedUrl ?? null;
}

export async function apagarMensagem(m: Mensagem) {
  trocar(m.conversa_id, m.id, { apagada: true, texto: '', anexo: null });
  if (!DEMO) await supabase?.rpc('mensagem_apagar', { p_id: m.id });
}
export async function silenciar(conv: string, on: boolean) {
  set({ conversas: st.conversas?.map(c => (c.id === conv ? { ...c, silenciada: on } : c)) ?? null });
  if (!DEMO) await supabase?.rpc('conversa_silenciar', { p_conversa: conv, p_silenciar: on });
}
/** Arquivar: a conversa sai da lista principal, continua recebendo e enviando, mas sem aviso e sem contar como não lida. */
export async function arquivar(conv: string, on: boolean) {
  set({ conversas: st.conversas?.map(c => (c.id === conv ? { ...c, arquivada: on } : c)) ?? null });
  if (!DEMO) { const { error } = await supabase!.rpc('conversa_arquivar', { p_conversa: conv, p_arquivar: on }); if (error) throw new Error(erroAmigavel(error)); }
}
export async function sairDoGrupo(conv: string) {
  if (!DEMO) { const { error } = await supabase!.rpc('conversa_sair', { p_conversa: conv }); if (error) throw new Error(erroAmigavel(error)); }
  set({ conversas: st.conversas?.filter(c => c.id !== conv) ?? null });
}
export async function membrosDa(conv: string): Promise<{ user_id: string; nome: string; admin: boolean }[]> {
  if (DEMO) return [{ user_id: 'eu', nome: 'Walter', admin: true }, { user_id: 'u-ana', nome: 'Ana Beatriz', admin: false }];
  const { data } = await supabase!.from('conversa_membros').select('user_id, nome, admin').eq('conversa_id', conv).order('nome');
  return (data ?? []) as any;
}
export async function mudarMembro(conv: string, user: string, incluir: boolean) {
  if (DEMO) return;
  const { error } = await supabase!.rpc('conversa_membro', { p_conversa: conv, p_user: user, p_incluir: incluir });
  if (error) throw new Error(erroAmigavel(error));
}

// ---------- abrir ou criar conversas ----------
const rpcId = async (fn: string, args: object) => {
  const { data, error } = await supabase!.rpc(fn, args);
  if (error) throw new Error(erroAmigavel(error));
  await carregarConversas();
  return data as string;
};
export async function conversaCom(outro: string, nome = '') {
  if (DEMO) {
    const id = 'd-' + outro;
    if (!st.conversas?.some(c => c.id === id)) set({ conversas: [{ id, tipo: 'dupla', nome, grupo_id: null, outro, ultima_em: agora(), ultima_txt: '', ultima_autor: null, nao_lidas: 0, silenciada: false, membros: 2, admin: false }, ...(st.conversas ?? [])] });
    return id;
  }
  return rpcId('conversa_dupla', { p_outro: outro });
}
export async function criarGrupo(nome: string, membros: string[]) {
  if (DEMO) {
    const id = 'g-' + uid();
    set({ conversas: [{ id, tipo: 'grupo', nome, grupo_id: null, outro: null, ultima_em: agora(), ultima_txt: t('criou o grupo'), ultima_autor: 'eu', nao_lidas: 0, silenciada: false, membros: membros.length + 1, admin: true }, ...(st.conversas ?? [])] });
    return id;
  }
  return rpcId('conversa_grupo_criar', { p_nome: nome, p_membros: membros });
}
export async function conversaDoGrupo(grupo: string) {
  if (DEMO) return 'turma';
  return rpcId('conversa_do_grupo', { p_grupo: grupo });
}

// ---------- amigos ----------
export async function carregarAmigos() {
  if (DEMO) { if (!st.amigos) demoInicial(); return; }
  const { data, error } = await supabase!.rpc('meus_amigos');
  if (error) { set({ erro: erroAmigavel(error) }); return; }
  set({ amigos: data as Amigo[] });
}
export async function pedirAmizade(codigo: string): Promise<{ ok: boolean; msg: string; id?: string }> {
  const c = codigo.trim().toUpperCase();
  if (!/^[A-Z0-9]{4,10}$/.test(c)) return { ok: false, msg: t('Confira o código. Ele tem 6 letras e números.') };
  if (DEMO) return { ok: true, msg: t('Pedido enviado. Quando a pessoa aceitar, vocês já podem conversar.') };
  const { data, error } = await supabase!.rpc('amigo_pedir', { p_codigo: c });
  if (error) return { ok: false, msg: erroAmigavel(error) };
  const r = data as { ok: boolean; erro?: string; status?: string; id?: string; nome?: string };
  await carregarAmigos();
  if (r.ok) return { ok: true, id: r.id, msg: r.status === 'aceita' ? t('{nome} agora é seu amigo. Já podem conversar.', { nome: r.nome ?? '' })
    : t('Pedido enviado para {nome}. Quando aceitar, vocês já podem conversar.', { nome: r.nome ?? '' }) };
  return { ok: false, msg: r.erro === 'proprio' ? t('Esse código é o seu.') : r.erro === 'bloqueada' ? t('Não é possível adicionar esta pessoa.') : t('Código não encontrado. Confira as letras e os números.') };
}
export async function responderAmizade(outro: string, acao: 'aceitar' | 'recusar' | 'bloquear' | 'desfazer') {
  if (DEMO) { set({ amigos: (st.amigos ?? []).map(a => (a.id === outro ? { ...a, status: acao === 'aceitar' ? 'aceita' : a.status } : a)).filter(a => acao === 'aceitar' || a.id !== outro) }); return; }
  const { error } = await supabase!.rpc('amigo_responder', { p_outro: outro, p_acao: acao });
  if (error) throw new Error(erroAmigavel(error));
  await carregarAmigos();
}

// ---------- chamada tocando ----------
export const pararDeTocar = () => set({ tocando: null });
export const tocarDemo = (x: NonNullable<Tocando>) => set({ tocando: x });
// prévia de demonstração no navegador: permite simular uma chamada chegando (testes)
if (DEMO && typeof globalThis !== 'undefined') (globalThis as any).__mpTocar = tocarDemo;
